import { useCallback, useEffect, useState } from 'react';
import * as React from 'react';
import classNames from 'classnames';
import { FormText, Image, Modal } from 'react-bootstrap';
import Quagga, {
  QuaggaJSConfigObject,
  QuaggaJSResultCallbackFunction,
  QuaggaJSResultObject,
} from '@ericblade/quagga2';

import {
  InputGroup,
  InputGroupAppend,
  InputGroupButton,
  Label,
  TextInput,
  TextInputProps,
} from '../../components/Input';

import classes from './add-warranty.module.scss';

import instructions from '../../assets/codice a barre esempio.jpg';

const quaggaConfig: QuaggaJSConfigObject = {
  inputStream: {
    type: 'LiveStream',
    constraints: {
      width: 640,
      height: 480,
      aspectRatio: 16 / 9,
      facingMode: 'environment', // or user
    },
  },
  locator: {
    patchSize: 'medium',
    halfSample: true,
  },
  numOfWorkers: navigator.hardwareConcurrency || 4,
  frequency: 10,
  decoder: {
    readers: ['ean_reader'],
    multiple: false,
  },
  locate: true,
};

type ScannerProps = {
  onDetected: QuaggaJSResultCallbackFunction;
  onError: () => void;
};

const Scanner = ({ onDetected, onError }: ScannerProps) => {
  useEffect(() => {
    Quagga.init(quaggaConfig, (err: any) => {
      if (err) {
        onError();
      } else {
        Quagga.start();
      }
    });

    Quagga.onDetected(onDetected);

    return () => {
      Quagga.stop();
      Quagga.offDetected(onDetected);
    };
  }, [onDetected, onError]);

  return (
    <div
      id={'interactive'}
      className={classNames('viewport', classes.barCodeScanner)}
    />
  );
};

type BarCodeScannerProps = {
  onDetected: (barcode: string) => void;
  onTextChange: TextInputProps['onTextChange'];
  value: TextInputProps['value'];
};

export const BarCodeScanner: React.FC<BarCodeScannerProps> = ({
  onDetected,
  onTextChange,
  value,
}) => {
  const [deviceIsSupported, setDeviceIsSupported] = useState(false);
  const [error, setError] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const onBarCodeChanged = useCallback(
    (result: QuaggaJSResultObject) => {
      onDetected(result.codeResult.code ?? '');
    },
    [onDetected]
  );

  const toggleScan = useCallback(() => {
    setScanning((prevState) => !prevState);
  }, []);

  const onError = useCallback(() => {
    setError(true);
    setScanning(false);
  }, []);

  useEffect(() => {
    const deviceHasSupport =
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      !navigator.userAgent.includes('wv');

    if (deviceHasSupport) {
      navigator.mediaDevices
        .enumerateDevices()
        .then((devices) => {
          return devices.some((device) => device.kind === 'videoinput');
        })
        .catch(() => false)
        .then(setDeviceIsSupported);
    }

    setDeviceIsSupported(false);
  }, []);

  return (
    <div>
      <Label>
        Codice prodotto *
        <br />
        <span
          className={classes.link}
          onClick={() => setShowInstructions(true)}
        >
          Dove trovo il codice?
        </span>
      </Label>

      <InputGroup>
        <TextInput onTextChange={onTextChange} value={value} />

        {deviceIsSupported && (
          <InputGroupAppend>
            <InputGroupButton onClick={toggleScan}>
              {scanning ? 'Interrompi scansione' : 'Scansiona'}
            </InputGroupButton>
          </InputGroupAppend>
        )}

        {error && (
          <FormText
            className={classNames(
              'text-muted',
              classes.barCodeScannerTextError
            )}
          >
            Per favore abilita i permessi della videocamera per poter
            scansionare i codici a barre
          </FormText>
        )}
      </InputGroup>

      {deviceIsSupported && scanning && (
        <Scanner onDetected={onBarCodeChanged} onError={onError} />
      )}

      <Modal
        centered
        onHide={() => setShowInstructions(false)}
        show={showInstructions}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Inserisci il codice a barre che trovi sulla scatola del prodotto
          </Modal.Title>
        </Modal.Header>

        <Image alt={'Dove trovo il codice a barre'} fluid src={instructions} />
      </Modal>
    </div>
  );
};
